import { Group } from 'two.js/src/group';
import Two from 'two.js';
import { Anchor } from 'two.js/src/anchor';
import { Rectangle } from 'two.js/src/shapes/rectangle';
import { Easing, Tween } from '@tweenjs/tween.js';
import coffin from '../../../assets/images/coffin.svg';

const COFFIN_TEXTURE_WIDTH = 400;
const COFFIN_TEXTURE_HEIGHT = 400;
const HALF_COFFIN_WIDTH = COFFIN_TEXTURE_WIDTH / 2;
const COFFIN_HALF_HEIGHT = COFFIN_TEXTURE_HEIGHT / 2;
const ZIGZAG_STEP = COFFIN_TEXTURE_HEIGHT/10;
const ZIGZAG_DEPTH = COFFIN_TEXTURE_WIDTH /14;

const createSeamPoints = (direction: 1 | -1) => ([
  new Anchor(0, -COFFIN_HALF_HEIGHT),
  new Anchor(direction * ZIGZAG_DEPTH, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP),
  new Anchor(0, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 2),
  new Anchor(direction * ZIGZAG_DEPTH, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 3),
  new Anchor(0, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 4),
  new Anchor(direction * ZIGZAG_DEPTH, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 5),
  new Anchor(0, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 6),
  new Anchor(direction * ZIGZAG_DEPTH*2, -COFFIN_HALF_HEIGHT + ZIGZAG_STEP * 7),
  new Anchor(0, COFFIN_HALF_HEIGHT),
]);

export default class Coffin extends Group {
  private readonly two: Two;

  private readonly body: Rectangle;

  private scene: Group;

  constructor(two: Two, scene: Group) {
    super();
    this.scene = scene;
    this.two = two;
    const texture = two.makeTexture(coffin);
    texture.repeat = 'no-repeat';

    this.body = new Rectangle(0, 0, COFFIN_TEXTURE_WIDTH, COFFIN_TEXTURE_HEIGHT);
    this.body.fill = texture;
    this.body.noStroke();
    this.add(this.body);
  }

  copy() {
    const copiedCoffin = new Coffin(this.two, this.scene);
    return copiedCoffin;
  }

  private createLeftHalfMask() {
    const seam = createSeamPoints(-1);
    const points = [
      new Anchor(-HALF_COFFIN_WIDTH, -COFFIN_HALF_HEIGHT),
      ...seam,
      new Anchor(-HALF_COFFIN_WIDTH, COFFIN_HALF_HEIGHT),
    ];

    const mask = this.two.makePath(points, true);
    mask.fill = '#ffffff';
    mask.noStroke();
    return mask;
  }

  private createRightHalfMask() {
    const seam = createSeamPoints(-1).reverse();
    const points = [
      ...seam,
      new Anchor(HALF_COFFIN_WIDTH, -COFFIN_HALF_HEIGHT),
      new Anchor(HALF_COFFIN_WIDTH, COFFIN_HALF_HEIGHT),
    ];

    const mask = this.two.makePath(points, true);
    mask.fill = '#ffffff';
    mask.noStroke();
    return mask;
  }

  leftHalf() {
    const leftHalfCoffin = this.copy();

    const mask = this.createLeftHalfMask();
    leftHalfCoffin.add(mask);
    leftHalfCoffin.mask = mask;

    return leftHalfCoffin;
  }

  rightHalf() {
    const rightHalfCoffin = this.copy();

    const mask = this.createRightHalfMask();
    rightHalfCoffin.add(mask);
    rightHalfCoffin.mask = mask;

    return rightHalfCoffin;
  }

  animateIn(coffinMoveTime: number, coffinOpenTime: number) {
    const shakeBurstTime = 110;
    const shakeRepeatDelay = Math.max(120, coffinOpenTime * 0.12);
    const shakeTime = (shakeBurstTime * 3) + (shakeRepeatDelay * 2);
    const spliTime = coffinOpenTime * 0.34;
    const restingPartsGap = 4;
    const randomOffset = (amount: number) => (Math.random() * amount * 2) - amount;
    const baseY = 0;
    const leftHalf = this.leftHalf();
    const rightHalf = this.rightHalf();
    leftHalf.visible=false;
    rightHalf.visible=false;
    this.add(leftHalf);
    this.add(rightHalf);


    const splitTween = new Tween( {i:0} ).to({i:100}, spliTime)
      .easing(Easing.Quadratic.In)
      .onStart(() => {
        this.translation.x = 0;
        this.body.visible=false;
        leftHalf.visible=true;
        rightHalf.visible=true;
        leftHalf.translation.x = -restingPartsGap;
        leftHalf.translation.y = 0;
        rightHalf.translation.x = restingPartsGap;
        rightHalf.translation.y = 0;
      }).onUpdate(proxy => {
        leftHalf.translation.x = -restingPartsGap - proxy.i;
        rightHalf.translation.x = restingPartsGap + proxy.i;
      });
    const fadeOutTween = new Tween( {opacity:1} ).to({opacity:0}, spliTime*0.5)
      .delay(spliTime*0.5)
      .easing(Easing.Quadratic.In)
      .onUpdate(proxy => {
        this.opacity = proxy.opacity;
      });

    const shakePartsTween = new Tween( {i:0} ).to({i:100}, shakeBurstTime)
      .repeat(2)
      .repeatDelay(shakeRepeatDelay)
      .easing(Easing.Back.Out)
      .onStart(() => {
        this.body.visible=false;
        leftHalf.visible=true;
        rightHalf.visible=true;
        leftHalf.translation.x = -restingPartsGap;
        leftHalf.translation.y = 0;
        rightHalf.translation.x = restingPartsGap;
        rightHalf.translation.y = 0;
      })
      .onRepeat(() => {
        leftHalf.translation.x = -restingPartsGap;
        leftHalf.translation.y = 0;
        rightHalf.translation.x = restingPartsGap;
        rightHalf.translation.y = 0;
      }).onUpdate(proxy => {
        const progress = proxy.i / 100;
        const kickStrength = Math.sin(progress * Math.PI) * 8;
        leftHalf.translation.x = -restingPartsGap - kickStrength + randomOffset(1.5);
        leftHalf.translation.y = randomOffset(4);
        rightHalf.translation.x = restingPartsGap + kickStrength + randomOffset(1.5);
        rightHalf.translation.y = randomOffset(4);
      })
      .onComplete(() => {
        leftHalf.translation.x = -restingPartsGap;
        leftHalf.translation.y = 0;
        rightHalf.translation.x = restingPartsGap;
        rightHalf.translation.y = 0;
      })
      .chain(splitTween, fadeOutTween);

    const shakeTween = new Tween( {i:0} ).to({i:100}, shakeBurstTime)
      .repeat(2)
      .repeatDelay(shakeRepeatDelay)
      .easing(Easing.Back.Out)
      .onStart(() => {
        this.translation.x = 0;
        this.translation.y = baseY;
      })
      .onRepeat(() => {
        this.translation.x = 0;
        this.translation.y = baseY;
      })
      .onUpdate(proxy => {
        const progress = proxy.i / 100;
        const kickStrength = Math.sin(progress * Math.PI) * 10;
        this.translation.x = randomOffset(2) + kickStrength;
        this.translation.y = baseY + randomOffset(1) - (kickStrength * 0.15);
      })
      .onComplete(() => {
        this.translation.x = 0;
        this.translation.y = baseY;
      })
      .chain(shakePartsTween);

    const upTween = new Tween({ y: 600})
      .to({
        y: 0,
      }, coffinMoveTime)
      .easing(Easing.Quadratic.Out)
      .onUpdate(proxy => {
        this.translation.x = 0;
        this.translation.y = proxy.y;
      })
      .chain(shakeTween)
      .start();
    return [upTween, shakeTween, shakePartsTween, splitTween, fadeOutTween];
  }
}
